import React, { useMemo, useState } from 'react';
import { Wallet, Percent, Clock, IndianRupee, Download } from 'lucide-react';

const formatINR = (n) => {
  if (!isFinite(n) || isNaN(n)) return '—';
  return Math.round(n).toLocaleString('en-IN');
};

const formatRate = (n) => {
  if (!isFinite(n) || isNaN(n)) return '—';
  return n.toFixed(2);
};

const formatTenure = (months) => {
  if (!isFinite(months) || isNaN(months) || months <= 0) return '—';
  const yrs = Math.floor(months / 12);
  const mos = Math.round(months % 12);
  if (yrs === 0) return `${mos} Mo`;
  if (mos === 0) return `${yrs} Yr`;
  return `${yrs} Yr ${mos} Mo`;
};

/* ── Solvers ── */
// EMI from P, annual_rate%, tenure_months
function calcEMI(P, annualRate, n) {
  if (P <= 0 || annualRate <= 0 || n <= 0) return 0;
  const r = annualRate / 12 / 100;
  const pow = Math.pow(1 + r, n);
  return (P * r * pow) / (pow - 1);
}

// Principal from EMI, annual_rate%, tenure_months
function calcAmount(emi, annualRate, n) {
  if (emi <= 0 || annualRate <= 0 || n <= 0) return 0;
  const r = annualRate / 12 / 100;
  const pow = Math.pow(1 + r, n);
  return (emi * (pow - 1)) / (r * pow);
}

// Tenure (months) from EMI, P, annual_rate%
function calcTenure(emi, P, annualRate) {
  if (emi <= 0 || P <= 0 || annualRate <= 0) return 0;
  const r = annualRate / 12 / 100;
  if (emi <= P * r) return Infinity; // EMI too small to ever repay
  return Math.log(emi / (emi - P * r)) / Math.log(1 + r);
}

// Annual rate% from EMI, P, tenure_months — Newton-Raphson
function calcRate(emi, P, n) {
  if (emi <= 0 || P <= 0 || n <= 0) return 0;
  // Quick bounds check
  if (emi * n <= P) return 0; // zero interest scenario
  let r = 0.01; // start at 1% monthly
  for (let i = 0; i < 2000; i++) {
    const pow = Math.pow(1 + r, n);
    const f = P * r * pow - emi * (pow - 1);
    // numerical derivative
    const dr = 1e-8;
    const pow2 = Math.pow(1 + r + dr, n);
    const f2 = P * (r + dr) * pow2 - emi * (pow2 - 1);
    const df = (f2 - f) / dr;
    if (Math.abs(df) < 1e-20) break;
    const rNew = r - f / df;
    if (rNew <= 0) { r = r / 2; continue; }
    if (Math.abs(rNew - r) < 1e-12) { r = rNew; break; }
    r = rNew;
  }
  return r * 12 * 100;
}

// Month-by-month repayment breakdown. The final payment is adjusted so the
// schedule closes the balance cleanly despite decimal calculations.
function buildAmortizationSchedule(principal, annualRate, months, payment) {
  if (
    !isFinite(principal) || !isFinite(annualRate) || !isFinite(months) ||
    !isFinite(payment) || principal <= 0 || annualRate < 0 || months <= 0 || payment <= 0
  ) return [];

  const monthlyRate = annualRate / 12 / 100;
  const schedule = [];
  let balance = principal;
  const totalMonths = Math.ceil(months);

  for (let month = 1; month <= totalMonths && balance > 0.01; month += 1) {
    const openingBalance = balance;
    const interest = openingBalance * monthlyRate;
    const principalPaid = Math.min(openingBalance, Math.max(0, payment - interest));
    const actualPayment = principalPaid + interest;
    balance = Math.max(0, openingBalance - principalPaid);

    schedule.push({
      month,
      openingBalance,
      payment: actualPayment,
      principalPaid,
      interest,
      closingBalance: balance,
    });

    // Avoid an unbounded table when an input EMI cannot reduce the balance.
    if (principalPaid <= 0) break;
  }

  return schedule;
}

const pdfMoney = (value) => `INR ${formatINR(value)}`;

const drawPdfHeader = (doc, pageTitle) => {
  // Compact vector wordmark so the downloaded PDF does not depend on a
  // third-party image URL or a browser-only font.
  doc.setFillColor(30, 115, 190);
  doc.rect(16, 17, 3, 13, 'F');
  doc.rect(22, 13, 3, 17, 'F');
  doc.rect(28, 9, 3, 21, 'F');
  doc.setDrawColor(46, 166, 144);
  doc.setLineWidth(1.2);
  doc.line(15, 28, 24, 20);
  doc.line(24, 20, 29, 22);
  doc.line(29, 22, 38, 10);
  doc.line(34.5, 10, 38, 10);
  doc.line(38, 10, 37, 14);

  doc.setTextColor(15, 61, 120);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(19);
  doc.text('FINIRIO', 44, 25);
  doc.setDrawColor(201, 162, 75);
  doc.setLineWidth(0.5);
  doc.line(16, 35, 194, 35);

  doc.setTextColor(15, 61, 120);
  doc.setFontSize(14);
  doc.text(pageTitle, 16, 45);
  doc.setTextColor(90, 102, 116);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('Support: support@finirio.com', 194, 45, { align: 'right' });
};

const downloadAmortizationPdf = async (schedule, summary) => {
  if (!schedule.length) return;

  // Keep the PDF library out of the initial calculator bundle; it is fetched
  // only when someone chooses to download their schedule.
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const columns = [
    { label: 'Month', x: 16, align: 'left' },
    { label: 'Opening', x: 47, align: 'right' },
    { label: 'EMI', x: 81, align: 'right' },
    { label: 'Principal', x: 115, align: 'right' },
    { label: 'Interest', x: 151, align: 'right' },
    { label: 'Closing', x: 194, align: 'right' },
  ];

  const drawTableHeader = (y) => {
    doc.setFillColor(15, 61, 120);
    doc.roundedRect(16, y - 5, 178, 9, 1.5, 1.5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    columns.forEach((column) => {
      doc.text(column.label, column.x, y + 0.5, { align: column.align });
    });
  };

  drawPdfHeader(doc, 'Amortization Schedule');
  doc.setTextColor(51, 65, 85);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Loan amount: ${pdfMoney(summary.principal)}`, 16, 55);
  doc.text(`Interest rate: ${summary.annualRate.toFixed(2)}% p.a.`, 78, 55);
  doc.text(`Tenure: ${summary.months} months`, 146, 55);

  let y = 68;
  drawTableHeader(y);
  y += 9;

  schedule.forEach((row, index) => {
    if (y > 281) {
      doc.addPage();
      drawPdfHeader(doc, 'Amortization Schedule');
      y = 55;
      drawTableHeader(y);
      y += 9;
    }

    if (index % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(16, y - 4.5, 178, 7, 'F');
    }
    doc.setTextColor(51, 65, 85);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text(String(row.month), columns[0].x, y, { align: columns[0].align });
    doc.text(pdfMoney(row.openingBalance), columns[1].x, y, { align: columns[1].align });
    doc.text(pdfMoney(row.payment), columns[2].x, y, { align: columns[2].align });
    doc.text(pdfMoney(row.principalPaid), columns[3].x, y, { align: columns[3].align });
    doc.text(pdfMoney(row.interest), columns[4].x, y, { align: columns[4].align });
    doc.text(pdfMoney(row.closingBalance), columns[5].x, y, { align: columns[5].align });
    y += 7;
  });

  doc.setTextColor(90, 102, 116);
  doc.setFontSize(8);
  doc.text(
    'Values are indicative and rounded to the nearest rupee. The final EMI may be adjusted slightly to close the remaining balance.',
    16,
    Math.min(y + 5, 290),
  );
  doc.save('finirio-amortization-schedule.pdf');
};

/* ── Shared Input ── */
const FieldInput = ({ label, icon: Icon, prefix, value, onChange, step = '1', placeholder = '' }) => (
  <div>
    <label className="block text-slate-700 text-sm font-medium mb-2">{label}</label>
    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden transition focus-within:border-[#1e73be] focus-within:ring-2 focus-within:ring-[#1e73be]/20">
      <span className="px-3 h-11 grid place-items-center text-slate-500 bg-[#fbf9f4] border-r border-slate-200 min-w-[44px]">
        {prefix || (Icon && <Icon className="w-4 h-4" />)}
      </span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 h-11 px-3 outline-none text-slate-800"
      />
    </div>
  </div>
);

/* Tenure input with Yr/Mo toggle */
const TenureInput = ({ label, value, onChange, unit, setUnit }) => (
  <div>
    <label className="block text-slate-700 text-sm font-medium mb-2">{label}</label>
    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden transition focus-within:border-[#1e73be] focus-within:ring-2 focus-within:ring-[#1e73be]/20">
      <span className="px-3 h-11 grid place-items-center text-slate-500 bg-[#fbf9f4] border-r border-slate-200">
        <Clock className="w-4 h-4" />
      </span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 h-11 px-3 outline-none text-slate-800"
      />
      <div className="flex border-l border-slate-200">
        {['Yr', 'Mo'].map((u) => (
          <button
            key={u}
            type="button"
            onClick={() => setUnit(u)}
            className={`px-3 h-11 text-sm font-medium transition-colors ${
              unit === u ? 'text-white' : 'text-slate-600 bg-white'
            }`}
            style={unit === u ? { backgroundColor: '#1e73be' } : undefined}
          >
            {u}
          </button>
        ))}
      </div>
    </div>
  </div>
);

/* Result row */
const ResultRow = ({ label, note, value, large }) => (
  <div>
    <p className="text-slate-600 text-[14px]">{label}</p>
    {note && <p className="text-slate-400 text-[12px]">{note}</p>}
    <p className={`text-slate-900 font-bold mt-1 ${large ? 'text-[26px]' : 'text-[21px]'}`}>{value}</p>
  </div>
);

const Divider = () => <div className="h-px bg-slate-200 my-4" />;

/* ── Donut chart ── */
const DonutChart = ({ principalPct, interestPct }) => {
  const R = 70, C = 2 * Math.PI * R;
  const ps = (principalPct / 100) * C;
  return (
    <div className="card-lux p-6">
      <h4 className="font-heading text-[#0b2545] text-center font-bold mb-3">Break-up of Total Payment</h4>
      <div className="flex items-center justify-center">
        <svg viewBox="0 0 240 220" className="w-[240px] h-[220px]">
          <circle cx="120" cy="110" r={R} fill="none" stroke="#1e73be" strokeWidth="36" />
          <circle cx="120" cy="110" r={R} fill="none" stroke="#c9a24b" strokeWidth="36"
            strokeDasharray={`${ps} ${C}`} transform="rotate(-90 120 110)" />
          <text x="30" y="70" fontSize="13" fontWeight="600" fill="#334155">{principalPct.toFixed(1)}%</text>
          <text x="165" y="165" fontSize="13" fontWeight="600" fill="#334155">{interestPct.toFixed(1)}%</text>
        </svg>
      </div>
      <div className="flex items-center justify-center gap-5 mt-2 text-[13px] text-slate-700">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#c9a24b' }} />Principal
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#1e73be' }} />Interest
        </div>
      </div>
    </div>
  );
};

const AmortizationSchedule = ({ schedule, summary }) => {
  if (!schedule.length) return null;

  const totalPrincipal = schedule.reduce((sum, row) => sum + row.principalPaid, 0);
  const totalInterest = schedule.reduce((sum, row) => sum + row.interest, 0);

  return (
    <section className="card-lux overflow-hidden" aria-labelledby="amortization-heading">
      <div className="navy-panel px-5 py-5 md:px-6 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="eyebrow-lux text-[#e8c875] mb-1">Repayment breakdown</p>
          <h3 id="amortization-heading" className="font-heading text-white text-xl font-bold">
            Amortization Schedule
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-white/70 text-sm">{schedule.length} monthly payments</p>
          <button
            type="button"
            onClick={() => void downloadAmortizationPdf(schedule, summary)}
            className="inline-flex items-center gap-2 rounded-lg bg-[#e8c875] px-3 py-2 text-xs font-bold text-[#0b2545] transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-white/70"
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Download PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4 md:p-6">
        {[
          ['Total Principal', totalPrincipal],
          ['Total Interest', totalInterest],
          ['First EMI', schedule[0].payment],
          ['Last EMI', schedule[schedule.length - 1].payment],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-[#fbf9f4] px-3 py-3">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-1 text-sm font-bold text-[#0b2545] md:text-base">
              <span className="mr-1">&#8377;</span>{formatINR(value)}
            </p>
          </div>
        ))}
      </div>

      <div className="max-h-[480px] overflow-auto border-t border-slate-200">
        <table className="min-w-[720px] w-full text-right text-sm">
          <caption className="sr-only">Monthly amortization schedule</caption>
          <thead className="sticky top-0 z-10 bg-white text-xs uppercase tracking-wide text-slate-500 shadow-sm">
            <tr>
              <th scope="col" className="px-4 py-3 text-left font-semibold">Month</th>
              <th scope="col" className="px-4 py-3 font-semibold">Opening Balance</th>
              <th scope="col" className="px-4 py-3 font-semibold">EMI</th>
              <th scope="col" className="px-4 py-3 font-semibold">Principal</th>
              <th scope="col" className="px-4 py-3 font-semibold">Interest</th>
              <th scope="col" className="px-4 py-3 font-semibold">Closing Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {schedule.map((row) => (
              <tr key={row.month} className="transition-colors hover:bg-[#fbf9f4]">
                <th scope="row" className="px-4 py-3 text-left font-semibold text-[#0b2545]">
                  {row.month}
                </th>
                <td className="px-4 py-3"><span className="mr-1">&#8377;</span>{formatINR(row.openingBalance)}</td>
                <td className="px-4 py-3 font-semibold text-[#0b2545]"><span className="mr-1">&#8377;</span>{formatINR(row.payment)}</td>
                <td className="px-4 py-3 text-[#1e73be]"><span className="mr-1">&#8377;</span>{formatINR(row.principalPaid)}</td>
                <td className="px-4 py-3 text-[#9b7625]"><span className="mr-1">&#8377;</span>{formatINR(row.interest)}</td>
                <td className="px-4 py-3"><span className="mr-1">&#8377;</span>{formatINR(row.closingBalance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-5 py-4 text-xs text-slate-500 md:px-6">
        The final EMI may be adjusted slightly to close the remaining balance. Values are indicative and rounded to the nearest rupee.
      </p>
    </section>
  );
};

/* ── MODES ── */
const MODES = [
  { id: 'emi',    label: 'Calc EMI' },
  { id: 'amount', label: 'Calc Amount' },
  { id: 'rate',   label: 'Calc ROI' },
  { id: 'tenure', label: 'Calc Tenure' },
];

/* ── Main Component ── */
const EMICalculator = () => {
  const [mode, setMode] = useState('emi');

  // Shared state for all 4 fields
  const [amount,  setAmount]  = useState(2500000);
  const [rate,    setRate]    = useState(10.5);
  const [tenure,  setTenure]  = useState(20);
  const [unit,    setUnit]    = useState('Yr');
  const [emiIn,   setEmiIn]   = useState(24869); // EMI input (for modes 2/3/4)

  const tenureMonths = unit === 'Yr' ? Number(tenure) * 12 : Number(tenure);

  /* computed results per mode */
  const result = useMemo(() => {
    const P   = Number(amount)  || 0;
    const R   = Number(rate)    || 0;
    const n   = tenureMonths;
    const EMI = Number(emiIn)   || 0;

    switch (mode) {
      case 'emi': {
        const emi   = calcEMI(P, R, n);
        const total = emi * n;
        const int   = total - P;
        const pPct  = P > 0 && total > 0 ? (P / total) * 100 : 100;
        return { emi, total, int, pPct, iPct: 100 - pPct, P };
      }
      case 'amount': {
        const principal = calcAmount(EMI, R, n);
        const total     = EMI * n;
        const int       = total - principal;
        const pPct      = principal > 0 && total > 0 ? (principal / total) * 100 : 100;
        return { principal, total, int, pPct, iPct: 100 - pPct, emi: EMI };
      }
      case 'rate': {
        const annualRate = calcRate(EMI, P, n);
        const total      = EMI * n;
        const int        = total - P;
        const pPct       = P > 0 && total > 0 ? (P / total) * 100 : 100;
        return { annualRate, total, int, pPct, iPct: 100 - pPct, emi: EMI };
      }
      case 'tenure': {
        const months = calcTenure(EMI, P, R);
        const total  = EMI * months;
        const int    = total - P;
        const pPct   = P > 0 && total > 0 ? (P / total) * 100 : 100;
        return { months, total, int, pPct, iPct: 100 - pPct, emi: EMI };
      }
      default: return {};
    }
  }, [mode, amount, rate, tenureMonths, emiIn]);

  /* ── Render inputs per mode ── */
  const renderInputs = () => {
    switch (mode) {
      case 'emi':
        return (
          <>
            <FieldInput label="Loan Amount" prefix={<span className="text-lg">&#8377;</span>}
              value={amount} onChange={setAmount} />
            <FieldInput label="Interest Rate (% per annum)" icon={Percent}
              value={rate} onChange={setRate} step="0.1" />
            <TenureInput label="Loan Tenure" value={tenure} onChange={setTenure} unit={unit} setUnit={setUnit} />
          </>
        );
      case 'amount':
        return (
          <>
            <FieldInput label="Monthly EMI" prefix={<span className="text-lg">&#8377;</span>}
              value={emiIn} onChange={setEmiIn} />
            <FieldInput label="Interest Rate (% per annum)" icon={Percent}
              value={rate} onChange={setRate} step="0.1" />
            <TenureInput label="Loan Tenure" value={tenure} onChange={setTenure} unit={unit} setUnit={setUnit} />
          </>
        );
      case 'rate':
        return (
          <>
            <FieldInput label="Monthly EMI" prefix={<span className="text-lg">&#8377;</span>}
              value={emiIn} onChange={setEmiIn} />
            <FieldInput label="Loan Amount" prefix={<span className="text-lg">&#8377;</span>}
              value={amount} onChange={setAmount} />
            <TenureInput label="Loan Tenure" value={tenure} onChange={setTenure} unit={unit} setUnit={setUnit} />
          </>
        );
      case 'tenure':
        return (
          <>
            <FieldInput label="Monthly EMI" prefix={<span className="text-lg">&#8377;</span>}
              value={emiIn} onChange={setEmiIn} />
            <FieldInput label="Loan Amount" prefix={<span className="text-lg">&#8377;</span>}
              value={amount} onChange={setAmount} />
            <FieldInput label="Interest Rate (% per annum)" icon={Percent}
              value={rate} onChange={setRate} step="0.1" />
          </>
        );
      default: return null;
    }
  };

  /* ── Render results per mode ── */
  const renderResults = () => {
    switch (mode) {
      case 'emi':
        return (
          <>
            <ResultRow large label="Monthly EMI"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.emi)}</>} />
            <Divider />
            <ResultRow label="Total Interest Payable"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.int)}</>} />
            <Divider />
            <ResultRow label="Total Payment" note="(Principal + Interest)"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.total)}</>} />
          </>
        );
      case 'amount':
        return (
          <>
            <ResultRow large label="Loan Amount (Principal)"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.principal)}</>} />
            <Divider />
            <ResultRow label="Total Interest Payable"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.int)}</>} />
            <Divider />
            <ResultRow label="Total Payment" note="(Principal + Interest)"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.total)}</>} />
          </>
        );
      case 'rate':
        return (
          <>
            <ResultRow large label="Annual Interest Rate"
              value={isFinite(result.annualRate) && result.annualRate > 0
                ? `${formatRate(result.annualRate)} %`
                : '—'} />
            <Divider />
            <ResultRow label="Total Interest Payable"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.int)}</>} />
            <Divider />
            <ResultRow label="Total Payment" note="(Principal + Interest)"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.total)}</>} />
          </>
        );
      case 'tenure':
        return (
          <>
            <ResultRow large label="Loan Tenure"
              value={isFinite(result.months) && result.months > 0
                ? formatTenure(result.months)
                : 'EMI too low'} />
            <Divider />
            <ResultRow label="Total Interest Payable"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.int)}</>} />
            <Divider />
            <ResultRow label="Total Payment" note="(Principal + Interest)"
              value={<><span className="mr-1">&#8377;</span>{formatINR(result.total)}</>} />
          </>
        );
      default: return null;
    }
  };

  const pPct = result.pPct ?? 100;
  const iPct = result.iPct ?? 0;
  const scheduleSummary = useMemo(() => {
    switch (mode) {
      case 'emi':
        return {
          principal: Number(amount) || 0,
          annualRate: Number(rate) || 0,
          months: tenureMonths,
          payment: result.emi,
        };
      case 'amount':
        return {
          principal: result.principal,
          annualRate: Number(rate) || 0,
          months: tenureMonths,
          payment: Number(emiIn) || 0,
        };
      case 'rate':
        return {
          principal: Number(amount) || 0,
          annualRate: result.annualRate,
          months: tenureMonths,
          payment: Number(emiIn) || 0,
        };
      case 'tenure':
        return {
          principal: Number(amount) || 0,
          annualRate: Number(rate) || 0,
          months: result.months,
          payment: Number(emiIn) || 0,
        };
      default:
        return {};
    }
  }, [mode, amount, rate, tenureMonths, emiIn, result]);

  const schedule = useMemo(
    () => buildAmortizationSchedule(
      scheduleSummary.principal,
      scheduleSummary.annualRate,
      scheduleSummary.months,
      scheduleSummary.payment,
    ),
    [scheduleSummary],
  );

  return (
    <div className="space-y-5">
      {/* Mode tabs */}
      <div className="flex flex-wrap gap-2 justify-center">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-all ${
              mode === m.id
                ? 'text-white border-transparent shadow-[0_8px_20px_-8px_rgba(30,115,190,.6)]'
                : 'text-slate-600 border-slate-200 bg-white hover:border-[#c9a24b]/50 hover:text-[#1e73be] hover:-translate-y-0.5'
            }`}
            style={mode === m.id ? { background: 'linear-gradient(135deg,#1e73be,#155a99)' } : undefined}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* Inputs */}
        <div className="card-lux overflow-hidden">
          <div className="px-5 py-3.5 navy-panel">
            <h3 className="font-heading text-white text-center text-[18px] font-bold tracking-wide">
              {MODES.find((m) => m.id === mode)?.label}
            </h3>
          </div>
          <div className="p-6 space-y-5">{renderInputs()}</div>
        </div>

        {/* Results */}
        <div className="card-lux p-8 flex flex-col justify-center text-center">
          {renderResults()}
        </div>

        {/* Donut */}
        <DonutChart principalPct={pPct} interestPct={iPct} />
      </div>

      <AmortizationSchedule schedule={schedule} summary={scheduleSummary} />
    </div>
  );
};

export default EMICalculator;
